import { useState, type FormEvent } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  SimpleGrid,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconSearch,
  IconTruck,
  IconTag,
  IconPrinter,
  IconDownload,
  IconExternalLink,
} from '@tabler/icons-react';
import { api } from '../lib/api';
import { num } from '../lib/format';

type SpecsSourceType = 'oem_api' | 'dealer' | 'grounded' | 'none';

interface PartDetails {
  part_number: string;
  manufacturer: string | null;
  description: string | null;
  product_type: string | null;
  category: string | null;
  weight_lbs: number | null;
  dimensions: {
    length_in: number | null;
    width_in: number | null;
    height_in: number | null;
  } | null;
  specs_source: string | null;
  specs_source_type: SpecsSourceType;
  n_clients_stocking: number;
  stocking_clients: string[] | null;
}

interface Address {
  zip: string;
  country?: string;
  state?: string;
  city?: string;
  street1?: string;
  street2?: string;
  name?: string;
  company?: string;
  phone?: string;
}

interface RateQuote {
  rate_id: string;
  carrier: string;
  service: string;
  rate_usd: number;
  currency: string;
  est_delivery_days: number | null;
  delivery_date: string | null;
}

interface PurchasedLabel {
  part_number: string;
  shipment_id: string;
  tracking_code: string | null;
  label_url: string | null;
  label_file_type: string | null;
  carrier: string;
  service: string;
  rate_usd: number;
  currency: string;
}

type ShippingEstimate =
  | {
      eligible: true;
      mode: 'parcel';
      weight_lbs: number;
      dimensions_in: { length: number; width: number; height: number };
      rates: RateQuote[];
      cheapest: RateQuote | null;
      easypost_shipment_id: string;
    }
  | {
      eligible: false;
      mode: 'parcel' | 'freight' | 'unknown';
      reason: string;
    };

interface ShippingResult {
  part_number: string;
  manufacturer?: string | null;
  description?: string | null;
  estimate?: ShippingEstimate;
  error?: string;
}

interface ShippingResponse {
  origin: Address;
  destination: Address;
  results: ShippingResult[];
}

const SOURCE_BADGE: Record<SpecsSourceType, { label: string; color: string } | null> = {
  oem_api: { label: 'OEM', color: 'teal' },
  dealer: { label: 'Dealer', color: 'blue' },
  grounded: { label: 'Grounded', color: 'yellow' },
  none: null,
};

const ORIGIN_STORAGE_KEY = 'parts-hub.shipping.origin';

const DEV_FROM: Address = {
  name: 'Parts Hub Warehouse',
  company: 'DSI Solutions',
  street1: '1 Apple Park Way',
  city: 'Cupertino',
  state: 'CA',
  zip: '95014',
  country: 'US',
  phone: '4085551234',
};

const DEV_TO: Address = {
  name: 'Test Recipient',
  street1: '20 W 34th St',
  city: 'New York',
  state: 'NY',
  zip: '10001',
  country: 'US',
  phone: '2125551234',
};

function loadSavedOrigin(): Address {
  try {
    const raw = localStorage.getItem(ORIGIN_STORAGE_KEY);
    if (raw) {
      return { zip: '', country: 'US', ...JSON.parse(raw) } as Address;
    }
  } catch {
    /* ignore */
  }
  return { zip: '', country: 'US', state: '', city: '' };
}

export default function ShippingPage() {
  const [partInput, setPartInput] = useState('');
  const [submittedPart, setSubmittedPart] = useState<string | null>(null);

  const [origin, setOrigin] = useState<Address>(loadSavedOrigin);
  const [destination, setDestination] = useState<Address>({
    zip: '',
    country: 'US',
    state: '',
    city: '',
  });

  const partQuery = useQuery({
    queryKey: ['part', submittedPart],
    enabled: !!submittedPart,
    queryFn: () => api<PartDetails>(`/api/parts/${encodeURIComponent(submittedPart!)}`),
    retry: false,
  });

  const shipMutation = useMutation({
    mutationFn: (vars: { partNumber: string; origin: Address; destination: Address }) =>
      api<ShippingResponse>(`/api/parts/shipping-estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin: vars.origin,
          destination: vars.destination,
          partNumbers: [vars.partNumber],
        }),
      }),
  });

  const [confirmOpen, confirmHandlers] = useDisclosure(false);
  const [labelOpen, labelHandlers] = useDisclosure(false);
  const [pendingRate, setPendingRate] = useState<RateQuote | null>(null);
  const [purchased, setPurchased] = useState<PurchasedLabel | null>(null);

  const [buyOrigin, setBuyOrigin] = useState<Address>(origin);
  const [buyDest, setBuyDest] = useState<Address>(destination);

  const openBuyModal = (rate: RateQuote) => {
    setPendingRate(rate);
    buyMutation.reset();
    // Pre-fill from the estimate form; user fills in the remaining fields
    setBuyOrigin({ ...origin });
    setBuyDest({ ...destination });
    confirmHandlers.open();
  };

  const buyMutation = useMutation({
    mutationFn: (vars: {
      partNumber: string;
      origin: Address;
      destination: Address;
      carrier: string;
      service: string;
    }) =>
      api<PurchasedLabel>(`/api/parts/${encodeURIComponent(vars.partNumber)}/shipping-label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin: vars.origin,
          destination: vars.destination,
          carrier: vars.carrier,
          service: vars.service,
        }),
      }),
    onSuccess: (data) => {
      setPurchased(data);
      confirmHandlers.close();
      setPendingRate(null);
      if (data.label_url) {
        labelHandlers.open();
      }
    },
  });

  const submitPart = (e: FormEvent) => {
    e.preventDefault();
    const pn = partInput.trim();
    if (!pn) {
      return;
    }
    shipMutation.reset();
    setSubmittedPart(pn);
  };

  const submitShip = (e: FormEvent) => {
    e.preventDefault();
    if (!submittedPart) {
      return;
    }
    if (origin.zip.trim().length < 3 || destination.zip.trim().length < 3) {
      return;
    }
    try {
      localStorage.setItem(ORIGIN_STORAGE_KEY, JSON.stringify(origin));
    } catch {
      /* ignore */
    }
    shipMutation.mutate({ partNumber: submittedPart, origin, destination });
  };

  const part = partQuery.data;
  const dims = part?.dimensions;
  const sourceBadge = part ? SOURCE_BADGE[part.specs_source_type] : null;
  const shipResult = shipMutation.data?.results[0];
  const estimate = shipResult?.estimate;
  const shipResultError = shipResult?.error;

  const setOriginField =
    <K extends keyof Address>(k: K) =>
    (v: string) =>
      setOrigin((o) => ({ ...o, [k]: v }));
  const setDestField =
    <K extends keyof Address>(k: K) =>
    (v: string) =>
      setDestination((d) => ({ ...d, [k]: v }));

  return (
    <Stack gap="md">
      <Title order={2}>Shipping Estimate</Title>

      <Card withBorder padding="md">
        <form onSubmit={submitPart}>
          <Group align="end" gap="sm">
            <TextInput
              label="Part number"
              placeholder="e.g. 2896823"
              leftSection={<IconSearch size={16} />}
              value={partInput}
              onChange={(e) => setPartInput(e.currentTarget.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            <Button
              type="submit"
              loading={partQuery.isFetching && partQuery.fetchStatus === 'fetching'}
            >
              Look up
            </Button>
          </Group>
        </form>
      </Card>

      {submittedPart && partQuery.isLoading && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {partQuery.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {partQuery.error instanceof Error ? partQuery.error.message : 'Failed to load part'}
        </Alert>
      )}

      {part && (
        <Card withBorder padding="md">
          <Group justify="space-between" mb="xs">
            <Group gap="sm">
              <Text ff="monospace" size="lg" fw={600}>
                {part.part_number}
              </Text>
              {sourceBadge && (
                <Badge
                  variant="light"
                  color={sourceBadge.color}
                  title={part.specs_source ?? undefined}
                >
                  {sourceBadge.label}
                </Badge>
              )}
              {part.n_clients_stocking > 0 && (
                <Badge variant="outline" color="orange">
                  Stocked by {part.n_clients_stocking}{' '}
                  {part.n_clients_stocking === 1 ? 'client' : 'clients'}
                </Badge>
              )}
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            <Field label="Manufacturer" value={part.manufacturer} />
            <Field label="Product type" value={part.product_type} />
            <Field label="Category" value={part.category} />
            <Field
              label="Weight"
              value={part.weight_lbs != null ? `${part.weight_lbs.toFixed(2)} lb` : null}
            />
            <Field
              label="L × W × H"
              value={
                dims?.length_in != null && dims.width_in != null && dims.height_in != null
                  ? `${dims.length_in} × ${dims.width_in} × ${dims.height_in} in`
                  : null
              }
            />
          </SimpleGrid>

          {part.description && (
            <>
              <Divider my="sm" />
              <Text size="sm" c="dimmed">
                {part.description}
              </Text>
            </>
          )}
        </Card>
      )}

      {part && (
        <Card withBorder padding="md">
          <form onSubmit={submitShip}>
            <Stack gap="md">
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Stack gap="sm">
                  <Text fw={600}>Ship from</Text>
                  <TextInput
                    label="ZIP"
                    required
                    value={origin.zip}
                    onChange={(e) => setOriginField('zip')(e.currentTarget.value)}
                  />
                  <SimpleGrid cols={2} spacing="sm">
                    <TextInput
                      label="State"
                      placeholder="OH"
                      value={origin.state ?? ''}
                      onChange={(e) => setOriginField('state')(e.currentTarget.value)}
                    />
                    <TextInput
                      label="Country"
                      placeholder="US"
                      value={origin.country ?? ''}
                      onChange={(e) => setOriginField('country')(e.currentTarget.value)}
                    />
                  </SimpleGrid>
                  <TextInput
                    label="City"
                    value={origin.city ?? ''}
                    onChange={(e) => setOriginField('city')(e.currentTarget.value)}
                  />
                </Stack>

                <Stack gap="sm">
                  <Text fw={600}>Ship to</Text>
                  <TextInput
                    label="ZIP"
                    required
                    value={destination.zip}
                    onChange={(e) => setDestField('zip')(e.currentTarget.value)}
                  />
                  <SimpleGrid cols={2} spacing="sm">
                    <TextInput
                      label="State"
                      value={destination.state ?? ''}
                      onChange={(e) => setDestField('state')(e.currentTarget.value)}
                    />
                    <TextInput
                      label="Country"
                      placeholder="US"
                      value={destination.country ?? ''}
                      onChange={(e) => setDestField('country')(e.currentTarget.value)}
                    />
                  </SimpleGrid>
                  <TextInput
                    label="City"
                    value={destination.city ?? ''}
                    onChange={(e) => setDestField('city')(e.currentTarget.value)}
                  />
                </Stack>
              </SimpleGrid>

              <Group justify="space-between">
                {import.meta.env.DEV ? (
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => {
                      setOrigin(DEV_FROM);
                      setDestination(DEV_TO);
                    }}
                  >
                    Dev: prefill addresses
                  </Button>
                ) : (
                  <span />
                )}
                <Button
                  type="submit"
                  leftSection={<IconTruck size={16} />}
                  loading={shipMutation.isPending}
                >
                  Get rates
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      )}

      {shipMutation.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {shipMutation.error instanceof Error ? shipMutation.error.message : 'Rate lookup failed'}
        </Alert>
      )}

      {shipResultError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {shipResultError}
        </Alert>
      )}

      {estimate && !estimate.eligible && (
        <Alert
          color={estimate.mode === 'freight' ? 'orange' : 'yellow'}
          icon={<IconAlertTriangle size={16} />}
          title={estimate.mode === 'freight' ? 'LTL freight required' : 'Cannot estimate shipping'}
        >
          {estimate.reason}
        </Alert>
      )}

      {estimate && estimate.eligible && (
        <Card withBorder padding={0}>
          <Group justify="space-between" p="md" pb="sm">
            <Text fw={600}>Rates ({estimate.rates.length})</Text>
            {estimate.cheapest && (
              <Text size="sm" c="dimmed">
                Cheapest:{' '}
                <Text span fw={600} c="teal">
                  ${estimate.cheapest.rate_usd.toFixed(2)}
                </Text>{' '}
                {estimate.cheapest.carrier} {estimate.cheapest.service}
              </Text>
            )}
          </Group>
          {estimate.rates.length === 0 ? (
            <Center p="xl">
              <Text c="dimmed">No carriers returned rates for this parcel.</Text>
            </Center>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Carrier</Table.Th>
                  <Table.Th>Service</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Rate</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Est. days</Table.Th>
                  <Table.Th>Delivery</Table.Th>
                  <Table.Th style={{ textAlign: 'center', width: 120 }}>Label</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {estimate.rates.map((r) => (
                  <Table.Tr key={r.rate_id}>
                    <Table.Td>{r.carrier}</Table.Td>
                    <Table.Td>{r.service}</Table.Td>
                    <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      ${r.rate_usd.toFixed(2)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{num(r.est_delivery_days)}</Table.Td>
                    <Table.Td>
                      {r.delivery_date ? new Date(r.delivery_date).toLocaleDateString() : '—'}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconTag size={14} />}
                        onClick={() => openBuyModal(r)}
                      >
                        Buy
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      )}

      {purchased && (
        <Alert color="teal" icon={<IconTag size={16} />} title="Label purchased">
          <Stack gap="xs">
            <Text size="sm">
              <strong>
                {purchased.carrier} {purchased.service}
              </strong>{' '}
              — ${purchased.rate_usd.toFixed(2)}
            </Text>
            {purchased.tracking_code && (
              <Text size="sm">
                Tracking: <Code>{purchased.tracking_code}</Code>
              </Text>
            )}
            {purchased.label_url && (
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  color="teal"
                  leftSection={<IconPrinter size={14} />}
                  onClick={() => labelHandlers.open()}
                >
                  View label
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  component="a"
                  href={purchased.label_url}
                  target="_blank"
                  rel="noreferrer"
                  leftSection={<IconDownload size={14} />}
                >
                  Download
                </Button>
              </Group>
            )}
          </Stack>
        </Alert>
      )}

      <Modal
        opened={labelOpen}
        onClose={labelHandlers.close}
        title="Shipping label"
        size="xl"
        centered
      >
        {purchased?.label_url && (
          <Stack gap="sm">
            <Group gap="xs" justify="flex-end">
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPrinter size={14} />}
                component="a"
                href={purchased.label_url}
                target="_blank"
                rel="noreferrer"
              >
                Open & print
              </Button>
              <Button
                size="xs"
                variant="subtle"
                component="a"
                href={purchased.label_url}
                leftSection={<IconDownload size={14} />}
              >
                Download PDF
              </Button>
              <Anchor href={purchased.label_url} target="_blank" rel="noreferrer" size="sm">
                <Group gap={4}>
                  <IconExternalLink size={14} /> New tab
                </Group>
              </Anchor>
            </Group>
            <iframe
              title="Shipping label"
              src={purchased.label_url}
              style={{
                width: '100%',
                height: '70vh',
                border: '1px solid var(--mantine-color-default-border)',
              }}
            />
            <Text size="xs" c="dimmed">
              For thermal (4×6) labels on a Zebra printer, switch the format to ZPL in code
              (currently PDF for letter-size printers).
            </Text>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={confirmOpen}
        onClose={() => {
          if (!buyMutation.isPending) {
            confirmHandlers.close();
            setPendingRate(null);
          }
        }}
        title="Purchase shipping label"
        centered
        size="lg"
      >
        {pendingRate && (
          <Stack gap="md">
            <Text size="sm">
              Buying a{' '}
              <strong>
                {pendingRate.carrier} {pendingRate.service}
              </strong>{' '}
              label for <strong>~${pendingRate.rate_usd.toFixed(2)}</strong> (final rate may adjust
              after address verification).
            </Text>
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />} variant="light">
              Carriers require a complete, verifiable address to print a label. Fill in any missing
              fields below. Production keys charge real money — test keys (EZTK…) produce free fake
              labels.
            </Alert>

            {import.meta.env.DEV && (
              <Group justify="flex-start">
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    setBuyOrigin(DEV_FROM);
                    setBuyDest(DEV_TO);
                  }}
                >
                  Dev: prefill with sample addresses
                </Button>
              </Group>
            )}

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Stack gap="xs">
                <Text fw={600} size="sm">
                  Ship from
                </Text>
                <TextInput
                  label="Name"
                  required
                  value={buyOrigin.name ?? ''}
                  onChange={(e) => setBuyOrigin((a) => ({ ...a, name: e.currentTarget.value }))}
                />
                <TextInput
                  label="Company"
                  value={buyOrigin.company ?? ''}
                  onChange={(e) => setBuyOrigin((a) => ({ ...a, company: e.currentTarget.value }))}
                />
                <TextInput
                  label="Street"
                  required
                  value={buyOrigin.street1 ?? ''}
                  onChange={(e) => setBuyOrigin((a) => ({ ...a, street1: e.currentTarget.value }))}
                />
                <TextInput
                  label="Street 2"
                  value={buyOrigin.street2 ?? ''}
                  onChange={(e) => setBuyOrigin((a) => ({ ...a, street2: e.currentTarget.value }))}
                />
                <SimpleGrid cols={2} spacing="xs">
                  <TextInput
                    label="City"
                    required
                    value={buyOrigin.city ?? ''}
                    onChange={(e) => setBuyOrigin((a) => ({ ...a, city: e.currentTarget.value }))}
                  />
                  <TextInput
                    label="State"
                    required
                    value={buyOrigin.state ?? ''}
                    onChange={(e) => setBuyOrigin((a) => ({ ...a, state: e.currentTarget.value }))}
                  />
                </SimpleGrid>
                <SimpleGrid cols={2} spacing="xs">
                  <TextInput
                    label="ZIP"
                    required
                    value={buyOrigin.zip}
                    onChange={(e) => setBuyOrigin((a) => ({ ...a, zip: e.currentTarget.value }))}
                  />
                  <TextInput
                    label="Phone"
                    value={buyOrigin.phone ?? ''}
                    onChange={(e) => setBuyOrigin((a) => ({ ...a, phone: e.currentTarget.value }))}
                  />
                </SimpleGrid>
              </Stack>

              <Stack gap="xs">
                <Text fw={600} size="sm">
                  Ship to
                </Text>
                <TextInput
                  label="Name"
                  required
                  value={buyDest.name ?? ''}
                  onChange={(e) => setBuyDest((a) => ({ ...a, name: e.currentTarget.value }))}
                />
                <TextInput
                  label="Company"
                  value={buyDest.company ?? ''}
                  onChange={(e) => setBuyDest((a) => ({ ...a, company: e.currentTarget.value }))}
                />
                <TextInput
                  label="Street"
                  required
                  value={buyDest.street1 ?? ''}
                  onChange={(e) => setBuyDest((a) => ({ ...a, street1: e.currentTarget.value }))}
                />
                <TextInput
                  label="Street 2"
                  value={buyDest.street2 ?? ''}
                  onChange={(e) => setBuyDest((a) => ({ ...a, street2: e.currentTarget.value }))}
                />
                <SimpleGrid cols={2} spacing="xs">
                  <TextInput
                    label="City"
                    required
                    value={buyDest.city ?? ''}
                    onChange={(e) => setBuyDest((a) => ({ ...a, city: e.currentTarget.value }))}
                  />
                  <TextInput
                    label="State"
                    required
                    value={buyDest.state ?? ''}
                    onChange={(e) => setBuyDest((a) => ({ ...a, state: e.currentTarget.value }))}
                  />
                </SimpleGrid>
                <SimpleGrid cols={2} spacing="xs">
                  <TextInput
                    label="ZIP"
                    required
                    value={buyDest.zip}
                    onChange={(e) => setBuyDest((a) => ({ ...a, zip: e.currentTarget.value }))}
                  />
                  <TextInput
                    label="Phone"
                    value={buyDest.phone ?? ''}
                    onChange={(e) => setBuyDest((a) => ({ ...a, phone: e.currentTarget.value }))}
                  />
                </SimpleGrid>
              </Stack>
            </SimpleGrid>

            {buyMutation.isError && (
              <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                {buyMutation.error instanceof Error ? buyMutation.error.message : 'Purchase failed'}
              </Alert>
            )}

            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={() => {
                  confirmHandlers.close();
                  setPendingRate(null);
                }}
                disabled={buyMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                color="teal"
                loading={buyMutation.isPending}
                onClick={() => {
                  if (!submittedPart || !pendingRate) {
                    return;
                  }
                  const requiredOk =
                    !!buyOrigin.name?.trim() &&
                    !!buyOrigin.street1?.trim() &&
                    !!buyOrigin.city?.trim() &&
                    !!buyOrigin.state?.trim() &&
                    !!buyOrigin.zip.trim() &&
                    !!buyDest.name?.trim() &&
                    !!buyDest.street1?.trim() &&
                    !!buyDest.city?.trim() &&
                    !!buyDest.state?.trim() &&
                    !!buyDest.zip.trim();
                  if (!requiredOk) {
                    return;
                  }
                  try {
                    localStorage.setItem(ORIGIN_STORAGE_KEY, JSON.stringify(buyOrigin));
                  } catch {
                    /* ignore */
                  }
                  buyMutation.mutate({
                    partNumber: submittedPart,
                    origin: buyOrigin,
                    destination: buyDest,
                    carrier: pendingRate.carrier,
                    service: pendingRate.service,
                  });
                }}
              >
                Confirm purchase
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="sm">
        {value ?? (
          <Text span c="dimmed">
            —
          </Text>
        )}
      </Text>
    </div>
  );
}
